import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SuperficieChambre, SurfaceDependance } from 'src/app/core/models/form-state.model';
import { Configuration } from '../configuration';
import { Price } from '../models/price.model';
import { PriceRequestModel } from '../models/priceRequest.model';
import { VyVTarifCreateRequest } from '../models/vyVTarifCreateRequest';
import { VyVTarifSaisieHabitation } from '../models/vyVTarifSaisieHabitation';
import { DistributorService } from './distributor.service';

@Injectable({
  providedIn: 'root'
})
export class PriceService {
  public configuration = new Configuration();
  baseUrl: string;
  apiEndpoint: string = 'prices/';

  private static readonly dependencyTypeMap: Record<string, string> = {
    [SurfaceDependance.Aucune]: 'None',
    [VyVTarifSaisieHabitation.SurfaceDependanceEnum.Aucune]: 'None',
    [SurfaceDependance.MoinsDe50m2]: '_0_50m2',
    [VyVTarifSaisieHabitation.SurfaceDependanceEnum.MoinsDe50m2]: '_0_50m2',
    [SurfaceDependance.MoinsDe40m2]: '_0_40m2',
    [VyVTarifSaisieHabitation.SurfaceDependanceEnum.MoinsDe40m2]: '_0_40m2',
    [SurfaceDependance.De50a100m2]: '_50_100m2',
    [VyVTarifSaisieHabitation.SurfaceDependanceEnum.De50a100m2]: '_50_100m2',
    [SurfaceDependance.PlusDe100m2]: '_100_250m2',
    [VyVTarifSaisieHabitation.SurfaceDependanceEnum.PlusDe100m2]: '_100_250m2'
  };

  constructor(
    private http: HttpClient,
    private distributorService: DistributorService,
    configuration: Configuration
  ) {
    this.baseUrl = configuration.apiBasePath ?? '';
  }

  public GetProductPrices(body: VyVTarifCreateRequest): Observable<Price> {
    let priceRequestModel = PriceService.ToPriceRequestModel(body);
    priceRequestModel.DistributorCode = this.distributorService.GetDistributorCode();
    return this.http.post<Price>(`${this.baseUrl}${this.apiEndpoint}`, priceRequestModel);
  }

  // private static getDependencyType(
  //   surface?: NatureResidenceEnum | null
  // ): NatureResidenceEnum | undefined {
  //   if (!surface) {
  //     return undefined;
  //   }

  //   return this.dependencyTypeMap[surface];
  // }

  private static getStreetNumberAndName(address: VyVTarifCreateRequest['SaisieAdresse']): string {
    return address?.NumeroEtRue
      ? address.NumeroEtRue
      : `${address.NumeroDeRue ?? ''} ${address.NomDeRue ?? ''}`.trim();
  }

  static ToPriceRequestModel(body: VyVTarifCreateRequest): PriceRequestModel {
    const address = body.SaisieAdresse;
    const streetNumberAndName = this.getStreetNumberAndName(address);
    const dependencyType = body.SaisieHabitation.SurfaceDependance;
    const floor = body.SaisieHabitation.Etage;
    const occupationStatus = body.SaisieHabitation.QualiteOccupant;

    const priceRequestModel: PriceRequestModel = {
      ProductCode: body.SaisieProduit.IdProduit,
      ContextId: body.Context,
      DistributorCode: '',
      PromotionCode: body.SaisieProduit.CodePromo ?? undefined,
      SponsorEmail: body.SaisieProduit.SponsorEmail ?? undefined,
      Email: body.Email ?? undefined,
      Address: {
        ZipCode: address?.CodePostal ?? '',
        City: address?.Ville ?? '',
        Country: 'France',
        StreetNumberAndName: streetNumberAndName
      },
      SubscriptionInformation: {
        housingType: body.SaisieHabitation.TypeHabitation,
        floor: floor || VyVTarifSaisieHabitation.EtageEnum.None,
        ResidenceType: body.SaisieHabitation.NatureResidence,
        OccupationStatus: occupationStatus,
        RoomCount: body.SaisieHabitation.NombrePieces,
        RoomCountGt40M2: body.SaisieHabitation.NombrePiecesSuperieures!,
        HasClaims: !!body.SaisieHabitation.AntecedentSinistresInondables,
        HasSwimmingPool: !!body.SaisieHabitation.PiscineSpaJacuzzi,
        HasVeranda: !!body.SaisieHabitation.AvecVeranda,
        VerandaType: !!body.SaisieHabitation.AvecVeranda
          ? VyVTarifSaisieHabitation.VerandaTypeEnum._0_10m2
          : VyVTarifSaisieHabitation.VerandaTypeEnum.None,
        DependencyType: dependencyType ?? SurfaceDependance.Aucune,
        HasGreenEnergyProductionInstallation: !!body.SaisieHabitation.InstallationDD,
        DeductibleType: '150'
      },
      CoverageDurationInMonths: body.CoverageDurationInMonths,
      RiskItems: [
        {
          Order: 1,
          TypeCode: 'HAB',
          Name: 'Habitation',
          Fields: {
            ADR_RISQUE: streetNumberAndName,
            ADR_COMP: '',
            ADR_CODE_POSTAL: address?.CodePostal ?? '',
            ADR_VILLE: address?.Ville ?? '',
            HAB_TYPE: body.SaisieHabitation.TypeHabitation,
            NATURE_LOG: body.SaisieHabitation.NatureResidence,
            NATURE_OCCU: occupationStatus,
            NBR_PIECES: body.SaisieHabitation.NombrePieces,
            ADR_ETAGE: body.SaisieHabitation.Etage,
            NBR_PIECES_40M2: body.SaisieHabitation.NombrePiecesSuperieures!,
            SURFACE_DEP: dependencyType,
            POSSEDE_ENERGIEV: !!body.SaisieHabitation.InstallationDD,
            POSSEDE_VERANDA: !!body.SaisieHabitation.AvecVeranda,
            POSSEDE_PISCINE: !!body.SaisieHabitation.PiscineSpaJacuzzi,
            REV_FISCAL: null,
            TYPE_CH: body.SaisieHabitation.TypeChambre ?? SuperficieChambre.Undefined,
            NBR_COLOC: body.SaisieHabitation.NombreColocataires,
            FRANCHISE: '150',
            ANNEE_CONSTRUCTION: 'None'
          }
        }
      ]
    };

    if (body.LessorName) {
      priceRequestModel.LessorName = body.LessorName;
    }

    return priceRequestModel;
  }
}
